import React, { useState, useCallback, memo } from 'react';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { useScrollTop } from '@alycecom/hooks';

import ProductModule from '../../../ProductModule';
import PageWithDataWrapper from '../../../../components/PageWithDataWrapper';
import GiftLayout from '../../../../components/Layout/GiftLayout';
import { getGiftHashId, getGiftProduct, getCanBeAccepted, getGiftGl } from '../../store/data/data.selectors';
import useInitGiftFlow from '../../hooks/useInitGiftFlow';
import useGiftActions from '../../hooks/useGiftActions';
import { getIsDraft, getIsPreview } from '../../store/statuses/statuses.selectors';
import useResponsive from '../../../../hooks/useResponsive';
import usePageLoadEvent from '../../../../hooks/usePageLoadEvent';
import useTrackRecipient from '../../../../hooks/useTrackRecipient';
import { TUseAcceptPolicyHandler } from '../../../../hooks/useAcceptPolicy';
import { getIsGiftDataLoaded } from '../../store/recipient.selectors';

import RecipientGiftActions from './RecipientGiftActions/RecipientGiftActions';
import GiftGreetingMessage from './GiftGreetingMessage';

export interface ILandingPageProps {
  onAcceptPolicy: TUseAcceptPolicyHandler;
}

const LandingPage = ({ onAcceptPolicy }: ILandingPageProps) => {
  const { url: parentUrl } = useRouteMatch();
  const { isMobile } = useResponsive();
  const trackEvent = useTrackRecipient();

  const isGiftDataLoaded = useSelector(getIsGiftDataLoaded);
  const hashId = useSelector(getGiftHashId);
  const sentProduct = useSelector(getGiftProduct);
  const canBeAccepted = useSelector(getCanBeAccepted);
  const isDraft = useSelector(getIsDraft);
  const isPreview = useSelector(getIsPreview);
  const gl = useSelector(getGiftGl);
  const productId = sentProduct && sentProduct.id;

  const [isGiftDetailsOpen, setIsGiftDetailsOpen] = useState(false);

  const openDetailsHandler = useCallback(() => {
    setIsGiftDetailsOpen(true);
    onAcceptPolicy('Product - Viewed', { product: sentProduct });
  }, [sentProduct, onAcceptPolicy]);

  const closeDetailsHandler = useCallback(() => {
    setIsGiftDetailsOpen(false);
    trackEvent('Product - Closed details', { product: sentProduct });
  }, [trackEvent, sentProduct]);

  useScrollTop();
  useInitGiftFlow();
  usePageLoadEvent('Landing page - Opened');

  const { handleAccept } = useGiftActions(parentUrl);
  const onSubmitProduct = useCallback(
    (options = '', optionsData) => {
      if (!isPreview) {
        handleAccept({ ...sentProduct, options, optionsData });
        trackEvent('Product - Click continue button', { product: sentProduct });
      }
    },
    [isPreview, handleAccept, sentProduct, trackEvent],
  );

  const sidebarWrapperProps = isDraft ? { wrapperProps: { mt: isMobile ? 10 : 5 } } : {};

  return (
    <PageWithDataWrapper isLoaded={isGiftDataLoaded}>
      <GiftLayout
        message={<GiftGreetingMessage />}
        actions={
          <RecipientGiftActions
            parentUrl={parentUrl}
            onAcceptPolicy={onAcceptPolicy}
            onGiftDetailsView={openDetailsHandler}
          />
        }
      />
      <ProductModule
        hashId={hashId}
        gl={gl}
        isOpen={isGiftDetailsOpen}
        productId={productId}
        onSubmit={onSubmitProduct}
        onClose={closeDetailsHandler}
        sidebarProps={sidebarWrapperProps}
        disabledContinue={!canBeAccepted}
      />
    </PageWithDataWrapper>
  );
};

export default memo(LandingPage);
